import { Component, Input, OnDestroy, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { forkJoin, of, Subject, Subscription, switchMap } from "rxjs";
import { catchError } from "rxjs/operators";
import { AdminService, BaseComponent, ContentService, LearnContentType, ProductSnapshot, UserService } from "@mypxplat/xplat/core";
import { LearnContentPickerComponent } from "../../learn-content-picker/learn-content-picker.component";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-associate-learn-content",
  templateUrl: "associate-learn-content.component.html",
  standalone: false,
})
export class AssociateLearnContentComponent extends BaseComponent implements OnDestroy {
  @Input() snapshot: ProductSnapshot;
  @ViewChild(LearnContentPickerComponent) learnPicker: LearnContentPickerComponent;

  public collapsed: boolean = false;
  public learnContent: any;
  public loading: boolean = false;
  public hasMetadata: boolean = false;
  public displayedSection: string = "videos";
  public pickerType: LearnContentType = "video";
  public pickerHint = "Start typing to find a video to associate with this product...";
  public pickerPlaceholder = "Enter a video title or ID...";
  public pickerExcludeIds: any[] = [];
  public videoAddedMap: any = {};
  private saveMetadata$ = new Subject<void>();
  private saveSubscription: Subscription;
  public associateWithProductLine: boolean = false;
  public productNoVideoSet: Set<number> = new Set();
  public productNoLessonSet: Set<number> = new Set();
  constructor(
    userService: UserService,
    public contentService: ContentService,
    private adminService: AdminService
  ) {
    super(userService);
    this.saveSubscription = this.saveMetadata$
      .pipe(
        switchMap(() => {
          const isProductLine = this.associateWithProductLine && this.snapshot.pimSpecs?.productNo;
          // Exclude productNo content from SKU saves to avoid duplication
          const videoIds = isProductLine ? (this.snapshot.metadata?.videos ?? []) : (this.snapshot.metadata?.videos ?? []).filter((id: number) => !this.productNoVideoSet.has(id));
          const lessonIds = isProductLine ? (this.snapshot.metadata?.lessons ?? []) : (this.snapshot.metadata?.lessons ?? []).filter((id: number) => !this.productNoLessonSet.has(id));
          // Real Product rows (id < 10000) get "id"; Fender/FMIC-only snapshots (id is the
          // 10-digit SKU) get "fender_sku" — see [[fmic-products-api]].
          const isRealProduct = Number(this.snapshot.product?.id) < 10000;
          return this.adminService.put(`content/update-product-metadata`, {
            stringId: isProductLine ? this.snapshot.pimSpecs.productNo : this.snapshot.product.stringId,
            productIdentifierType: isProductLine ? "product_no" : isRealProduct ? "id" : "fender_sku",
            videoIds,
            lessonIds,
          });
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.hasMetadata = this.snapshot.metadata?.videos || this.snapshot.metadata?.courses || this.snapshot.metadata?.lessons;
    this.loading = true;
    if (this.snapshot.pimSpecs?.productNo) {
      this.associateWithProductLine = true;
    }
    if (this.snapshot.metadata?.productNoVideos) {
      this.productNoVideoSet = new Set(this.snapshot.metadata.productNoVideos);
    }
    if (this.snapshot.metadata?.productNoLessons) {
      this.productNoLessonSet = new Set(this.snapshot.metadata.productNoLessons);
    }
    if (this.snapshot.metadata?.videos) {
      this.snapshot.metadata.videos.forEach((video: any) => {
        this.videoAddedMap[video?.id ?? video] = true;
      });
    }
    this.updatePicker();
    const videoIds = this.snapshot.metadata?.videos || [];
    const lessonIds = this.snapshot.metadata?.lessons || [];
    const videos$ = videoIds.length ? forkJoin(videoIds.map((id) => this.contentService.getAdminLearnVideo(id))) : of([]);
    const lessons$ = lessonIds.length ? forkJoin(lessonIds.map((id) => this.contentService.getAdminLearnGroup(id).pipe(catchError(() => of(null))))) : of([]);
    forkJoin([videos$, lessons$]).subscribe(() => (this.loading = false));
  }

  updatePicker() {
    const isVideos = this.displayedSection == "videos";
    this.pickerType = isVideos ? "video" : "lesson";
    this.pickerHint = isVideos ? "Start typing to find a video to associate with this product..." : "Start typing to find a lesson to associate with this product...";
    this.pickerPlaceholder = isVideos ? "Enter a video title or ID..." : "Enter a lesson title or ID...";
    this.pickerExcludeIds = isVideos ? this.snapshot.metadata?.videos || [] : this.snapshot.metadata?.lessons || [];
  }

  addLesson(lesson) {
    this.hasMetadata = true;
    if (!this.snapshot.metadata) this.snapshot.metadata = {};
    if (!this.snapshot.metadata.lessons) this.snapshot.metadata.lessons = [];
    this.snapshot.metadata.lessons.push(lesson.id);
    this.learnPicker?.removeItem(lesson.id);
    this.updatePicker();
    this.saveMetadata$.next();
  }

  addVideo(video) {
    this.hasMetadata = true;
    if (!this.snapshot.metadata) this.snapshot.metadata = {};
    if (!this.snapshot.metadata.videos) this.snapshot.metadata.videos = [];
    this.snapshot.metadata.videos.push(video.id);
    this.videoAddedMap[video.id] = true;
    this.learnPicker?.removeItem(video.id);
    this.updatePicker();
    this.saveMetadata$.next();
  }

  removeVideo(videoId) {
    this.snapshot.metadata.videos = this.snapshot.metadata.videos.filter((id) => id != videoId);
    this.updatePicker();
    this.saveMetadata$.next();
  }

  removeLesson(lessonId) {
    this.snapshot.metadata.lessons = this.snapshot.metadata.lessons.filter((id) => id != lessonId);
    this.updatePicker();
    this.saveMetadata$.next();
  }

  isFromProductLine(id: number, type: string): boolean {
    if (type === "video") return this.productNoVideoSet.has(id);
    if (type === "lesson") return this.productNoLessonSet.has(id);
    return false;
  }

  switchLearnContentSection(section: any) {
    this.displayedSection = section.id;
    this.updatePicker();
  }

  onPickerAdded(item) {
    if (this.displayedSection == "videos") this.addVideo(item);
    else this.addLesson(item);
  }

  ngOnDestroy(): void {
    this.saveSubscription?.unsubscribe();
    super.ngOnDestroy();
  }
}
